import { prettifyMeasurementKey } from "@/utils/annotationMeasurementUtils";
import type { SegmentationFilterThresholds } from "./Segmentation/helpers";

/** The segmentation filters a reviewer can activate; ids match the sidebar sliders. */
export type SegmentationFilterId = "sensitivity" | "maxLength" | "cameraDistance" | "bboxLength" | "maxOverlapIou";

/**
 * A snapshot of the segmentation filter panel. Only filters listed in `activated`
 * are applied — the other threshold values auto-track each image's data maxima and
 * carry no user intent, so they never count as a changed setting.
 */
export interface SegmentationFilterSettings extends SegmentationFilterThresholds {
  activated: SegmentationFilterId[];
}

/**
 * Per-filter display strings, the single source of truth for BOTH the sidebar
 * slider read-outs and the persist-dialog change lines. `format` takes the MODEL
 * value (e.g. raw confidence for sensitivity) and applies any inversion itself, so
 * both surfaces render byte-identical text from the same function.
 */
export const SEGMENTATION_FILTER_DISPLAY: Record<
  SegmentationFilterId,
  { sliderLabel: string; changeLabel: string; format: (value: number) => string }
> = {
  sensitivity: {
    sliderLabel: "Sensitivity",
    changeLabel: "Sensitivity",
    format: (v) => `${Math.round((1 - v) * 100)}%`,
  },
  maxLength: { sliderLabel: "Max Length (cm)", changeLabel: "Max Length", format: (v) => `${(v * 100).toFixed(0)}cm` },
  cameraDistance: {
    sliderLabel: "Camera Distance (m)",
    changeLabel: "Camera Distance",
    format: (v) => `${v.toFixed(2)}m`,
  },
  bboxLength: { sliderLabel: "Max Length (px)", changeLabel: "Max Length", format: (v) => `${Math.round(v)}px` },
  maxOverlapIou: { sliderLabel: "Max Overlap", changeLabel: "Max Overlap", format: (v) => `${Math.round(v * 100)}%` },
};

/** On-screen-only viewer settings that can persist across image navigation. */
export interface AnnotationViewSettings {
  showMask: boolean;
  segmentationFilters: SegmentationFilterSettings | null;
  /** Stand-count keypoint sensitivity; null when the viewer has no keypoints. */
  keypointConfidenceThreshold: number | null;
  /** Stand-count dot size relative to the original radius; display-only. */
  keypointSizePercent: number;
  /** Measurement axes the reviewer parked ("length", "width", …). */
  hiddenMeasurementKeys: string[];
  /** Edit-mode declutter: draw each box as a center point and drop masks/keypoints. */
  collapseBoxesToPoints: boolean;
  /** Edit-mode sweep: zoom to one object at a time and step through them with the arrow keys. */
  reviewObjectsMode: boolean;
  /** Show annotations whose geometry falls outside the persisted measurement boundary. */
  showOutsideBoundaryAnnotations: boolean;
}

/**
 * Session-scoped answers to in-viewer review prompts. Never persisted to storage — cleared on
 * refresh and on the same task/trait/org identity change as {@link ViewSettingsSession}.
 *
 * Kept separate from `ViewSettingsSession` deliberately: that object is rebuilt from fresh literals
 * at several call sites, so a field added there would be silently dropped by any site that forgot
 * to spread the previous value.
 */
export type ObjectReviewSession = {
  suppressEndOfObjectsPrompt: boolean;
};

export const DEFAULT_OBJECT_REVIEW_SESSION: ObjectReviewSession = {
  suppressEndOfObjectsPrompt: false,
};

/** How the reviewer answered the once-per-session "keep these settings?" prompt. */
export type ViewSettingsPersistChoice = "unasked" | "persist" | "reset";

/**
 * Session-level answer to the "keep these view settings?" prompt plus the last
 * snapshot to restore. Owned by the page so it survives sheet close/reopen and
 * cross-plot navigation, and clears on route change / refresh.
 */
export type ViewSettingsSession = {
  choice: ViewSettingsPersistChoice;
  settings: AnnotationViewSettings | null;
};

export const DEFAULT_ANNOTATION_VIEW_SETTINGS: AnnotationViewSettings = {
  showMask: true,
  segmentationFilters: null,
  keypointConfidenceThreshold: null,
  keypointSizePercent: 75,
  hiddenMeasurementKeys: [],
  collapseBoxesToPoints: false,
  reviewObjectsMode: false,
  showOutsideBoundaryAnnotations: false,
};

/**
 * The max for a data-derived filter slider (max length / camera distance / bbox length).
 * Extends to the image's data maximum, but never below the current (possibly restored,
 * possibly out-of-image-range) value — a Radix slider with `value > max` clamps the thumb
 * and jumps on first adjustment. Floors to a positive so an active zero-threshold on an
 * image with no matching data stays a valid range (max > min).
 */
export const resolveFilterSliderMax = (dataMax: number | null, value: number): number =>
  Math.max(dataMax ?? 0, value) || 1;

/**
 * True when any tracked setting differs from its default (inactive filter values
 * never count). Derived from `describeChangedViewSettings` so the persist gate and
 * the dialog contents can never diverge — the prompt can never open with an empty
 * bullet list (e.g. an activated filter whose value is still null produces no line
 * and therefore does not count as non-default).
 */
export function hasNonDefaultViewSettings(settings: AnnotationViewSettings | null): boolean {
  return settings != null && describeChangedViewSettings(settings).length > 0;
}

/**
 * Whether navigating to another image should first prompt the reviewer to keep
 * or reset their view settings. We only ask on the very first navigation of the
 * session (`choice` still "unasked") and only when something is actually
 * non-default; once answered ("persist" or "reset") we never ask again.
 */
export function shouldAskToPersistViewSettings(
  choice: ViewSettingsPersistChoice,
  settings: AnnotationViewSettings | null
): boolean {
  return choice === "unasked" && hasNonDefaultViewSettings(settings);
}

/**
 * One human-readable line per changed setting, in a stable order and formatted
 * exactly like the AnnotationSidebar slider read-outs. `resolveAxisLabel` turns a
 * measurement key into its display name (defaults to `prettifyMeasurementKey`).
 */
export function describeChangedViewSettings(
  settings: AnnotationViewSettings,
  resolveAxisLabel: (key: string) => string = prettifyMeasurementKey
): string[] {
  const lines: string[] = [];

  if (settings.showMask === false) lines.push("Mask hidden");
  if (settings.keypointSizePercent !== DEFAULT_ANNOTATION_VIEW_SETTINGS.keypointSizePercent) {
    lines.push(`Point size ${settings.keypointSizePercent}%`);
  }
  if (settings.collapseBoxesToPoints) lines.push("Boxes collapsed to points");
  if (settings.reviewObjectsMode) lines.push("Review objects mode");
  if (settings.showOutsideBoundaryAnnotations) {
    lines.push("Outside-boundary annotations included in counts");
  }

  const filters = settings.segmentationFilters;
  if (filters) {
    const activated = new Set<SegmentationFilterId>(filters.activated);
    // A filter contributes a line only when activated AND its value is present.
    // sensitivity/maxOverlapIou are always numeric; the null-gate only skips the
    // three distance filters whose value auto-tracks (and starts null).
    const pushLine = (id: SegmentationFilterId, value: number | null) => {
      if (!activated.has(id) || value == null) return;
      const { changeLabel, format } = SEGMENTATION_FILTER_DISPLAY[id];
      lines.push(`${changeLabel} ${format(value)}`);
    };
    pushLine("sensitivity", filters.confidenceThreshold);
    pushLine("maxLength", filters.maxLengthM);
    pushLine("cameraDistance", filters.maxCameraDistanceM);
    pushLine("bboxLength", filters.maxBboxLengthPx);
    pushLine("maxOverlapIou", filters.maxOverlapIou);
  }

  if (settings.keypointConfidenceThreshold != null && settings.keypointConfidenceThreshold > 0) {
    lines.push(
      `Stand count sensitivity ${SEGMENTATION_FILTER_DISPLAY.sensitivity.format(settings.keypointConfidenceThreshold)}`
    );
  }

  if (settings.hiddenMeasurementKeys.length > 0) {
    lines.push(`Hidden measurements: ${settings.hiddenMeasurementKeys.map(resolveAxisLabel).join(", ")}`);
  }

  return lines;
}
